// src/components/shared/index.tsx
import { useEffect, useState } from "react";
import { TicketStatus, TicketPriority } from "@/types";

// ─── Badge ────────────────────────────────────────────────────────

export function StatusBadge({ status }: { status: TicketStatus }) {
  const map: Record<TicketStatus, { cls?: string; style?: React.CSSProperties; label: string }> = {
    open:        { cls: "badge badge-open",     label: "Open" },
    in_progress: { cls: "badge badge-progress", label: "In Progress" },
    pending_user:{ cls: "badge", style: { background: "var(--orange-l)", color: "var(--orange-t)" }, label: "Pending User Info" },
    resolved:    { cls: "badge badge-resolved", label: "Resolved" },
    closed:      { cls: "badge badge-closed",   label: "Closed" },
  };
  const entry = map[status] ?? { cls: "badge", label: status as string };
  return <span className={entry.cls} style={entry.style}>{entry.label}</span>;
}

// Priority shape uses both color AND a leading glyph so users with
// color-vision deficiency can still distinguish levels at a glance.
// Each glyph is aria-hidden — the priority label itself ("P1") is
// still read by screen readers.
export function PriorityBadge({ priority }: { priority: TicketPriority }) {
  const map: Record<TicketPriority, { cls: string; label: string; glyph: string; aria: string }> = {
    P1: { cls: "badge badge-p1", label: "P1", glyph: "▲", aria: "Priority 1, critical" },
    P2: { cls: "badge badge-p2", label: "P2", glyph: "△", aria: "Priority 2, high" },
    P3: { cls: "badge badge-p3", label: "P3", glyph: "■", aria: "Priority 3, medium" },
    P4: { cls: "badge badge-p4", label: "P4", glyph: "▽", aria: "Priority 4, low" },
  };
  const entry = map[priority] ?? { cls: "badge", label: priority, glyph: "", aria: priority };
  return (
    <span className={entry.cls} aria-label={entry.aria}>
      {entry.glyph && <span aria-hidden="true">{entry.glyph}</span>}
      {entry.label}
    </span>
  );
}

export function AiTriageBadge() {
  return <span className="badge badge-gem">AI Triaged</span>;
}

// ─── Avatar ───────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#0071e3","#5e5ce6","#ff9500","#28cd41","#ff3b30","#6e5cf6","#1a73e8",
];

export function Avatar({
  name, size = 28, url,
}: { name: string; size?: number; url?: string }) {
  const initials = name
    .split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");
  const color = AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

  if (url) {
    return (
      <img
        src={url} alt={name}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }}
      />
    );
  }

  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: color,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.36), fontWeight: 700, color: "white",
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────

export function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      className={`toggle ${value ? "on" : ""}`}
      onClick={() => onChange(!value)}
      aria-pressed={value}
      type="button"
    />
  );
}

// ─── Spinner ──────────────────────────────────────────────────────

export function Spinner({ size = 16, color = "var(--blue)" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      style={{ fill: "none", animation: "spin 0.8s linear infinite" }}
    >
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ─── Toast ────────────────────────────────────────────────────────

interface ToastMsg { id: number; message: string; type: "success" | "error" | "info" }
let toastId = 0;
let _addToast: ((msg: Omit<ToastMsg, "id">) => void) | null = null;

export function toast(message: string, type: ToastMsg["type"] = "success") {
  _addToast?.({ message, type });
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);

  useEffect(() => {
    _addToast = (msg) => {
      const id = ++toastId;
      setToasts((prev) => [...prev, { ...msg, id }]);
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
    };
    return () => { _addToast = null; };
  }, []);

  const bg: Record<ToastMsg["type"], string> = {
    success: "var(--green)",
    error: "var(--red)",
    info: "var(--blue)",
  };

  return (
    <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
      {toasts.map((t) => (
        <div key={t.id} className="fade-in" style={{
          background: bg[t.type], color: "white",
          padding: "10px 16px", borderRadius: 10,
          fontSize: 13, fontFamily: "var(--font)",
          boxShadow: "var(--shadow-md)", maxWidth: 340,
        }}>
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────

export function EmptyState({ icon, title, subtitle, action }: {
  icon?: string; title: string; subtitle?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", textAlign: "center" }}>
      {icon && <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>}
      <h3 style={{ marginBottom: 4 }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16 }}>{subtitle}</p>}
      {action && <button className="btn btn-primary" onClick={action.onClick}>{action.label}</button>}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────

export function SectionHeader({ title, action }: { title: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
      <h3>{title}</h3>
      {action && (
        <button onClick={action.onClick} style={{ fontSize: 13, color: "var(--blue)", background: "none", border: "none", cursor: "pointer" }}>
          {action.label}
        </button>
      )}
    </div>
  );
}

// ─── Confirmation modal ───────────────────────────────────────────

export function ConfirmModal({ title, message, confirmLabel = "Confirm", danger = false, onConfirm, onCancel }: {
  title: string; message: string; confirmLabel?: string;
  danger?: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 200, backdropFilter: "blur(4px)",
    }}>
      <div className="card fade-in" style={{ width: 380, overflow: "hidden" }}>
        <div className="card-hdr"><h3>{title}</h3></div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16 }}>{message}</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} onClick={onConfirm}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
